import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/PersonExternalAssociations.Services';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonExternalAssociations.Actions';

// models
import { ErrorType } from '../../models/HttpError';
import { ExternalEntityAssociationViewModel, ExternalEntityAssociationCreateCommand, ExternalEntityAssociationUpdateCommand } from '../../models/Entities/Person/ExternalAssociation/ExternalAssociationModels';

export type KnownAction =
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetAllRequestAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetAllSuccessAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetAllFailureAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetByIdRequestAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetByIdSuccessAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationGetByIdFailureAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationCreateRequestAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationCreateSuccessAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationCreateFailureAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationUpdateRequestAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationUpdateSuccessAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationUpdateFailureAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationDeleteRequestAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationDeleteSuccessAction
    | Actions.PersonExternalAssociations.PersonExternalAssociationDeleteFailureAction;

const NotAuthorizedError: ErrorType = {
    ErrorCode: 401,
    ErrorMessage: 'Not authorized',
    Errors: []
};

export const actionCreators = {
    GetExternalAssociationsByPersonId: (personId: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETALL_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETALL_REQUEST, personId });

        Services.GetExternalAssociationsByPersonId(token, personId)
            .then((response: ExternalEntityAssociationViewModel[]) => {
                dispatch({
                    type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETALL_SUCCESS,
                    personId,
                    payload: response.map((item: ExternalEntityAssociationViewModel) => ({ ...item, personId }))
                });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETALL_FAILURE, error });
            });
    },

    GetExternalAssociationById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETBYID_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETBYID_REQUEST, id });

        Services.GetExternalAssociationById(token, id)
            .then((response: ExternalEntityAssociationViewModel) => {
                dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETBYID_SUCCESS, payload: response });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_GETBYID_FAILURE, error });
            });
    },

    CreateExternalAssociation: (payload: ExternalEntityAssociationCreateCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_CREATE_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_CREATE_REQUEST, payload });

        Services.CreateExternalAssociation(token, payload)
            .then((created: ExternalEntityAssociationViewModel) => {
                dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_CREATE_SUCCESS, payload: { ...created, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_CREATE_FAILURE, error });
            });
    },

    UpdateExternalAssociation: (payload: ExternalEntityAssociationUpdateCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_UPDATE_FAILURE, id: payload.id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_UPDATE_REQUEST, payload });

        Services.UpdateExternalAssociation(token, payload)
            .then((updated: ExternalEntityAssociationViewModel) => {
                dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_UPDATE_SUCCESS, payload: { ...updated, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_UPDATE_FAILURE, id: payload.id, error });
            });
    },

    DeleteExternalAssociation: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_DELETE_FAILURE, id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_DELETE_REQUEST, id });

        Services.DeleteExternalAssociation(token, id)
            .then(() => {
                dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_DELETE_SUCCESS, id });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_EXTERNAL_ASSOCIATION_DELETE_FAILURE, id, error });
            });
    }
};
